import {
  freecell,
  SUIT_SYMBOLS,
  type Card,
  type FreecellMoveDest,
  type FreecellMoveSource,
  type FreecellView,
} from '@cardengine/shared';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../store.js';
import { PlayingCard } from './PlayingCard.js';

/** Two sources are the same pick-up if they name the same cards. */
function sameSource(a: FreecellMoveSource, b: FreecellMoveSource): boolean {
  if (a.zone !== b.zone) return false;
  if (a.zone === 'tableau' && b.zone === 'tableau') return a.index === b.index && a.depth === b.depth;
  if (a.zone === 'free' && b.zone === 'free') return a.index === b.index;
  if (a.zone === 'foundation' && b.zone === 'foundation') return a.suit === b.suit;
  return false;
}

function sameDest(a: FreecellMoveDest, b: FreecellMoveDest): boolean {
  if (a.zone !== b.zone) return false;
  if (a.zone === 'tableau' && b.zone === 'tableau') return a.index === b.index;
  if (a.zone === 'free' && b.zone === 'free') return a.index === b.index;
  if (a.zone === 'foundation' && b.zone === 'foundation') return a.suit === b.suit;
  return false;
}

/**
 * What the player has hold of.
 *
 * Simpler than Klondike's, which needs a second arm for the face-down cards.
 * FreeCell deals everything face up, so anything on the table is a real card
 * with a real reason it will or will not move. `liftable` records whether the
 * engine would allow it, which decides between a move and a bounce.
 */
interface Grip {
  from: FreecellMoveSource;
  cards: Card[];
  liftable: boolean;
  key: string;
}

/** Matches the CSS bounce. */
const BOUNCE_MS = 420;

export function FreecellBoard() {
  const view = useStore((s) => s.freecell);
  const move = useStore((s) => s.freecellMove);
  const autoComplete = useStore((s) => s.freecellAutoComplete);
  const pushToast = useStore((s) => s.pushToast);
  const highlightPlayable = useStore((s) => s.highlightPlayable);
  const setHighlightPlayable = useStore((s) => s.setHighlightPlayable);

  const [grip, setGrip] = useState<Grip | null>(null);
  const [bouncing, setBouncing] = useState<string | null>(null);
  const bounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The same sensors as the other two tables, so a tap still reads as a click
  // and a drag needs a deliberate pull.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const moveNumber = view?.moveNumber ?? 0;
  useEffect(() => setGrip(null), [moveNumber]);

  useEffect(
    () => () => {
      if (bounceTimer.current) clearTimeout(bounceTimer.current);
    },
    [],
  );

  if (!view) return <p className="game__loading">Dealing…</p>;

  const moves = view.legalMoves;
  const canLift = (from: FreecellMoveSource) => moves.some((m) => sameSource(m.from, from));

  /** Only a genuinely liftable grip lights anything up. */
  const heldSource = grip?.liftable ? grip.from : null;
  const canDrop = (to: FreecellMoveDest) =>
    heldSource !== null && moves.some((m) => sameSource(m.from, heldSource) && sameDest(m.to, to));

  const liftableCount = new Set(moves.map((m) => JSON.stringify(m.from))).size;

  function refuse(key: string, message: string): void {
    pushToast(message, 'warn');
    if (bounceTimer.current) clearTimeout(bounceTimer.current);
    setBouncing(key);
    bounceTimer.current = setTimeout(() => setBouncing(null), BOUNCE_MS);
    setGrip(null);
  }

  /** Settle a grip against a destination: move it, or bounce and say why. */
  function resolve(held: Grip, to: FreecellMoveDest): void {
    if (!held.liftable) {
      refuse(held.key, freecell.describeUnliftable(held.cards));
      return;
    }
    if (moves.some((m) => sameSource(m.from, held.from) && sameDest(m.to, to))) {
      move(held.from, to);
      return;
    }
    // Two refusals turn on where the card came *from*, which the shared
    // `describeRefusal` cannot see — it is handed a destination and the board.
    if (to.zone === 'free' && held.from.zone !== 'tableau') {
      refuse(
        held.key,
        held.from.zone === 'free'
          ? 'That card is already in a cell.'
          : 'A banked card only comes back down to the tableau.',
      );
      return;
    }
    refuse(held.key, freecell.describeRefusal(view as FreecellView, held.cards, to));
  }

  /** Click to lift; click again to bank it or put it down. */
  function tap(next: Grip): void {
    if (grip && sameSource(grip.from, next.from) && grip.liftable) {
      const home = moves.find((m) => sameSource(m.from, next.from) && m.to.zone === 'foundation');
      if (home) move(next.from, home.to);
      else setGrip(null);
      return;
    }
    if (!next.liftable) {
      refuse(next.key, freecell.describeUnliftable(next.cards));
      return;
    }
    setGrip(next);
  }

  function handleDragStart(event: DragStartEvent): void {
    const held = event.active.data.current?.grip as Grip | undefined;
    if (held) setGrip(held);
  }

  function handleDragEnd(event: DragEndEvent): void {
    const held = event.active.data.current?.grip as Grip | undefined;
    const to = event.over?.data.current?.to as FreecellMoveDest | undefined;
    setGrip(null);
    // Dropped on open felt: no move, and nothing worth complaining about.
    if (held && to) resolve(held, to);
  }

  const bounceClass = (key: string) => (bouncing === key ? 'card--bounce' : '');

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setGrip(null)}
    >
      <div className="freecell" aria-label="FreeCell table">
        <div className="freecell__top">
          <div className="freecell__cells" aria-label="Free cells">
            {view.free.map((card, index) => (
              <CellSlot
                key={index}
                index={index}
                card={card}
                held={heldSource}
                showTarget={highlightPlayable && canDrop({ zone: 'free', index })}
                bounceClass={bounceClass}
                onActivate={(next) => (grip ? resolve(grip, { zone: 'free', index }) : next && tap(next))}
              />
            ))}
          </div>

          <div className="freecell__foundations" aria-label="Foundations">
            {view.foundations.map((foundation) => (
              <FoundationSlot
                key={foundation.suit}
                foundation={foundation}
                held={heldSource}
                showTarget={highlightPlayable && canDrop({ zone: 'foundation', suit: foundation.suit })}
                bounceClass={bounceClass}
                onActivate={(next) =>
                  grip ? resolve(grip, { zone: 'foundation', suit: foundation.suit }) : tap(next)
                }
              />
            ))}
          </div>
        </div>

        <div className="freecell__tableau" aria-label="Tableau">
          {view.tableau.map((column, index) => (
            <Column
              key={index}
              index={index}
              column={column}
              held={heldSource}
              lifted={grip !== null}
              liftedFromHere={grip?.from.zone === 'tableau' && grip.from.index === index}
              highlight={highlightPlayable}
              showTarget={highlightPlayable && canDrop({ zone: 'tableau', index })}
              canLift={canLift}
              bounceClass={bounceClass}
              onTap={tap}
              onDropInto={(to) => grip && resolve(grip, to)}
            />
          ))}
        </div>

        <div className="freecell__status">
          {view.canAutoComplete && (
            <button type="button" className="btn btn--primary" onClick={autoComplete}>
              Finish it
            </button>
          )}
          <span className="freecell__hint">{hintFor(view, liftableCount, highlightPlayable)}</span>
          <label className="assist">
            <input
              type="checkbox"
              checked={highlightPlayable}
              onChange={(event) => setHighlightPlayable(event.target.checked)}
            />
            <span>Highlight playable cards</span>
          </label>
        </div>
      </div>

      {/*
        A full-size overlay follows the cursor, exactly as the other two tables
        do. Without it the browser draws its own shrunken, washed-out ghost.
      */}
      <DragOverlay dropAnimation={null}>
        {grip ? (
          <div className="drag-overlay drag-overlay--solitaire">
            {grip.cards.map((card, i) => (
              <PlayingCard
                key={card.id}
                card={card}
                style={{ marginTop: i === 0 ? 0 : 'calc(var(--card-w) * -1.09)' }}
              />
            ))}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * The line under the board.
 *
 * Says how long a run will carry, because that is FreeCell's one piece of
 * hidden arithmetic — everything else about the position is on the table, but
 * "why will this ladder not move" is answered by a number the player would
 * otherwise have to work out.
 */
function hintFor(view: FreecellView, liftableCount: number, highlight: boolean): string {
  if (view.canAutoComplete) return 'Nothing is in the way — the rest plays itself.';
  if (liftableCount === 0) return 'Every cell is full and nothing will move. That deal is done.';

  const cells = view.cellsFree === 0 ? 'No cells free' : `${view.cellsFree} free`;
  const run = view.maxMoveSize === 1 ? 'one card at a time' : `runs of ${view.maxMoveSize}`;
  const arithmetic = `${cells} · ${run}`;

  if (highlight) return `${liftableCount} card${liftableCount === 1 ? '' : 's'} can move · ${arithmetic}`;
  return `${arithmetic} · drag a card, or click to lift it`;
}

interface DraggableCardProps {
  id: string;
  grip: Grip;
  className: string;
  style?: React.CSSProperties;
  label: string;
  onActivate: (grip: Grip) => void;
  children: React.ReactNode;
}

/**
 * One liftable thing on the table.
 *
 * No transform is applied to the source — the DragOverlay draws the card that
 * follows the cursor, so moving this one as well would drag a second ghost
 * along with it.
 */
function DraggableCard({ id, grip, className, style, label, onActivate, children }: DraggableCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, data: { grip } });

  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`${className} ${isDragging ? 'is-dragging' : ''}`}
      style={style}
      {...listeners}
      {...attributes}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onActivate(grip);
      }}
    >
      {children}
    </button>
  );
}

interface CellSlotProps {
  index: number;
  card: Card | null;
  held: FreecellMoveSource | null;
  showTarget: boolean;
  bounceClass: (key: string) => string;
  /** Null when the cell is empty — there is nothing to lift out of it. */
  onActivate: (grip: Grip | null) => void;
}

function CellSlot({ index, card, held, showTarget, bounceClass, onActivate }: CellSlotProps) {
  const to: FreecellMoveDest = { zone: 'free', index };
  const { setNodeRef } = useDroppable({ id: `drop-free-${index}`, data: { to } });

  const key = `free:${index}`;
  const from: FreecellMoveSource = { zone: 'free', index };

  return (
    <div
      ref={setNodeRef}
      className={`freecell__cell ${showTarget ? 'is-target' : ''}`}
      onClick={() => !card && onActivate(null)}
    >
      {card ? (
        <DraggableCard
          id={card.id}
          grip={{ from, cards: [card], liftable: true, key }}
          className="freecell__cellcard"
          label={`${card.rank}${SUIT_SYMBOLS[card.suit]} in cell ${index + 1}`}
          onActivate={onActivate}
        >
          <PlayingCard
            card={card}
            className={bounceClass(key)}
            selected={held?.zone === 'free' && held.index === index}
          />
        </DraggableCard>
      ) : (
        <span className="freecell__slot" aria-label={`Empty cell ${index + 1}`} />
      )}
    </div>
  );
}

interface FoundationSlotProps {
  foundation: FreecellView['foundations'][number];
  held: FreecellMoveSource | null;
  showTarget: boolean;
  bounceClass: (key: string) => string;
  onActivate: (grip: Grip) => void;
}

function FoundationSlot({ foundation, held, showTarget, bounceClass, onActivate }: FoundationSlotProps) {
  const to: FreecellMoveDest = { zone: 'foundation', suit: foundation.suit };
  const { setNodeRef } = useDroppable({ id: `drop-foundation-${foundation.suit}`, data: { to } });

  const top = foundation.top;
  const key = `foundation:${foundation.suit}`;
  const from: FreecellMoveSource = { zone: 'foundation', suit: foundation.suit };

  return (
    <div ref={setNodeRef} className={`freecell__foundation ${showTarget ? 'is-target' : ''}`}>
      {top ? (
        <DraggableCard
          id={top.id}
          grip={{ from, cards: [top], liftable: true, key }}
          className="freecell__foundationcard"
          label={`${top.rank}${SUIT_SYMBOLS[top.suit]} on the ${foundation.suit} foundation`}
          onActivate={onActivate}
        >
          <PlayingCard
            card={top}
            className={bounceClass(key)}
            selected={held?.zone === 'foundation' && held.suit === foundation.suit}
          />
        </DraggableCard>
      ) : (
        <span className="freecell__slot freecell__slot--suit">{SUIT_SYMBOLS[foundation.suit]}</span>
      )}
    </div>
  );
}

interface ColumnProps {
  index: number;
  column: Card[];
  held: FreecellMoveSource | null;
  lifted: boolean;
  /** The card in hand came out of this column. */
  liftedFromHere: boolean;
  highlight: boolean;
  showTarget: boolean;
  canLift: (from: FreecellMoveSource) => boolean;
  bounceClass: (key: string) => string;
  onTap: (grip: Grip) => void;
  onDropInto: (to: FreecellMoveDest) => void;
}

function Column({
  index,
  column,
  held,
  lifted,
  liftedFromHere,
  highlight,
  showTarget,
  canLift,
  bounceClass,
  onTap,
  onDropInto,
}: ColumnProps) {
  const to: FreecellMoveDest = { zone: 'tableau', index };
  const { setNodeRef } = useDroppable({ id: `drop-tableau-${index}`, data: { to } });

  return (
    <div
      ref={setNodeRef}
      className="freecell__column"
      // Clicking the felt of the column a card came out of puts it down rather
      // than trying to drop it back where it already is.
      onClick={() => lifted && !liftedFromHere && onDropInto(to)}
      aria-label={`Column ${index + 1}`}
    >
      {column.length === 0 && <span className="freecell__slot freecell__slot--column" />}

      {/*
        A card-sized outline at the exact spot the run will land, rather than a
        border around the whole column — a column is tall and mostly empty, so
        outlining it drew a huge box around nothing.
      */}
      {showTarget && (
        <span
          className="freecell__landing"
          style={{ top: `calc(${column.length} * var(--stack-step))` }}
          aria-hidden="true"
        />
      )}

      {column.map((card, i) => {
        // Depth counts from the exposed end of the column upwards.
        const depth = column.length - i;
        const from: FreecellMoveSource = { zone: 'tableau', index, depth };
        const run = column.slice(i);
        const key = `tableau:${index}:${depth}`;
        const movable = canLift(from);
        const isHeld = held?.zone === 'tableau' && held.index === index && held.depth >= depth;

        return (
          <DraggableCard
            key={card.id}
            id={card.id}
            grip={{ from, cards: run, liftable: movable, key }}
            className="freecell__stacked"
            style={{ top: `calc(${i} * var(--stack-step))` }}
            label={`${card.rank}${SUIT_SYMBOLS[card.suit]} in column ${index + 1}`}
            // Clicking a card in the column you lifted from is a change of
            // mind, not a move: re-lift at this depth, bank it, or put it
            // down. Routing it through the drop path instead would refuse it
            // with a baffling line about the run stacking on itself.
            onActivate={(next) => (lifted && !liftedFromHere ? onDropInto(to) : onTap(next))}
          >
            <PlayingCard
              card={card}
              className={bounceClass(key)}
              playable={highlight && movable}
              dimmed={highlight && !movable}
              selected={isHeld}
            />
          </DraggableCard>
        );
      })}
    </div>
  );
}

/** Exported for the header readout. */
export function describeCells(cellsFree: number, maxMoveSize: number): string {
  const cells = `${cellsFree} cell${cellsFree === 1 ? '' : 's'} free`;
  return `${cells} · ${maxMoveSize === 1 ? 'one card' : `${maxMoveSize} cards`} at a time`;
}
